import React, { useState, useCallback, useEffect } from "react";
import CommonFormLabel from "../../components/formLabel";
import Input from "../../components/input";
import ValidationFeedback from "../../components/validationFeedback";
import CommonButton from "../../components/commonButton";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import { Card, Toast, Form } from "react-bootstrap";
import { Formik } from "formik";
import { updateBusinessType, getBusinessTypeDetails } from "../../modules/BusinessType/businessTypeSlice";
import { businessTypeEditSchema } from "../../schema/validationSchemas";

export default function Edit() {
  const { id: businessTypeId } = useParams();
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const dispatch = useDispatch();
  const [businessType, setBusinessType] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchBusinessType = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dispatch(getBusinessTypeDetails(businessTypeId));
      setBusinessType(result.payload.data);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching businessType:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, businessTypeId, setLoading]);

  useEffect(() => {
    setBusinessType(null);
    if (businessTypeId) {
      fetchBusinessType();
    }
  }, [businessTypeId, fetchBusinessType]);

  const handleCancel = () => {
    setBusinessType(null);
    navigate("/business-types");
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    const updatedValues = { ...values, id: businessTypeId };

    const formData = new FormData();
    formData.append("id", updatedValues.id);
    formData.append("_method", "PUT");
    formData.append("name", updatedValues.name);

    await dispatch(updateBusinessType(formData))
      .then((res) => {
        if (res.payload && res.payload.success) {
          localStorage.setItem("business_type_notify_message", res.payload.message);
          setSnackbarMessage(res.payload.message);
          setSnackbarVariant("success");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
          setBusinessType(null);
          navigate("/business-types");
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(res.payload.message || "Failed to update business type!");
          setSnackbarVariant("danger");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        setSnackbarMessage("Failed to update business type!");
        setSnackbarVariant("danger");
        setShowSnackbar(true);
        setSubmitting(false);
        setIsSubmitting(false);
      });
  };

  if (!businessType)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <h2>Loading Business type Details...</h2>
      </div>
    );

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>

      {businessType && (
        <Formik
          initialValues={{
            name: businessType.name || "",
          }}
          validationSchema={businessTypeEditSchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            handleChange,
            handleSubmit,
            values,
            setFieldValue,
          }) => (
            <Form
              onSubmit={handleSubmit}
              encType="multipart/form-data"
              className="bg-white shadow-lg rounded-lg p-4"
            >
              <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
                <h4 className="mb-4 d-flex justify-content-center">Basic</h4>
                <div className="mb-4">
                  <CommonFormLabel htmlFor="name" title="Name" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Enter Name"
                    onChange={handleChange}
                    value={values.name}
                    className={`shadow-none ${
                      errors?.name && touched.name
                        ? "error"
                        : "correct"
                    }`}
                  />
                  {touched.name && errors?.name && (
                    <ValidationFeedback title={errors?.name} />
                  )}
                </div>
              </Card>

              <div className="d-flex align-items-center justify-content-end gap-3 common-popup-btn">
                <CommonButton
                  title={isSubmitting ? "Updating..." : "Update"}
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary primary-shadow"
                />
                <CommonButton
                  title="Cancel"
                  onClick={handleCancel}
                  className="btn-muted"
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
}
